import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Employee, LeaveRequest, AttendanceRecord, PayrollRecord, Job, Candidate, PerformanceRecord, TrainingProgram, Department, OnboardingEmployee, Notification, User, OnboardingConfig } from '../../types';
import { apiClient } from '../../api/apiClient';

interface DataState {
  users: User[];
  employees: Employee[];
  leaveRequests: LeaveRequest[];
  attendance: AttendanceRecord[];
  payroll: PayrollRecord[];
  jobs: Job[];
  candidates: Candidate[];
  performance: PerformanceRecord[];
  training: TrainingProgram[];
  departments: Department[];
  onboarding: OnboardingEmployee[];
  onboardingConfig: OnboardingConfig | null;
  notifications: Notification[];
  loading: boolean;
  error: string | null;
}

const initialState: DataState = {
  users: [], employees: [], leaveRequests: [], attendance: [], payroll: [],
  jobs: [], candidates: [], performance: [], training: [], departments: [],
  onboarding: [], onboardingConfig: null, notifications: [], loading: false, error: null,
};

// Fetch all data dynamically from API
export const fetchAllData = createAsyncThunk('data/fetchAll', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get('/data/all');
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch data');
  }
});

// Example Async Thunk: Create Employee
export const createEmployeeAsync = createAsyncThunk(
  'data/createEmployee',
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/employees', payload);
      return res.data; // Assuming backend returns the created employee with ID
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create employee');
    }
  }
);

// Example Async Thunk: Update Leave Status
export const updateLeaveStatusAsync = createAsyncThunk(
  'data/updateLeaveStatus',
  async ({ id, status }: { id: string; status: string }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/leaves/${id}/status`, { status });
      return res.data; // Assuming backend returns updated leave request
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update leave request');
    }
  }
);

export const removeEmployeeAsync = createAsyncThunk(
  'data/removeEmployee',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/employees/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete employee');
    }
  }
);

export const addLeaveRequestAsync = createAsyncThunk(
  'data/addLeaveRequest',
  async (leave: Omit<LeaveRequest, 'id'>, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const currentUserId = state.auth.user?._id;
      // Find the employee ObjectId associated with this user
      const emp = state.data.employees.find((e: any) => e.user?._id === currentUserId || e.user === currentUserId);
      const employeeObjectId = emp ? (emp._id || emp.id) : leave.empId;

      // Map to backend schema
      const payload = {
        employee: employeeObjectId,
        leaveType: leave.type,
        startDate: leave.startDate,
        endDate: leave.endDate,
        reason: leave.reason
      };

      const res = await apiClient.post('/leaves', payload);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add leave request');
    }
  }
);

export const addJobAsync = createAsyncThunk(
  'data/addJob',
  async (job: Omit<Job, 'id'>, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/jobs', job);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add job');
    }
  }
);

export const addCandidateAsync = createAsyncThunk(
  'data/addCandidate',
  async (candidate: any, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/candidates', candidate);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add candidate');
    }
  }
);

export const moveCandidateStageAsync = createAsyncThunk(
  'data/moveCandidateStage',
  async ({ id, stage }: { id: string; stage: string }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/candidates/${id}/stage`, { stage });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update candidate stage');
    }
  }
);

export const toggleOnboardingTaskAsync = createAsyncThunk(
  'data/toggleOnboardingTask',
  async ({ id, taskIdx }: { id: string; taskIdx: number }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/onboarding/${id}/task`, { taskIdx });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to toggle task');
    }
  }
);

export const toggleHrChecklistAsync = createAsyncThunk(
  'data/toggleHrChecklist',
  async (taskIdx: number, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/onboarding/config/checklist/${taskIdx}`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to toggle HR checklist task');
    }
  }
);

export const markNotificationReadAsync = createAsyncThunk(
  'data/markNotificationRead',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/notifications/${id}/read`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to mark notification read');
    }
  }
);

export const addTrainingProgramAsync = createAsyncThunk(
  'data/addTrainingProgram',
  async (program: any, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/trainings', program);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create training program');
    }
  }
);

export const enrollTrainingAsync = createAsyncThunk(
  'data/enrollTraining',
  async (trainingId: string, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const currentUserId = state.auth.user?._id;
      const emp = state.data.employees.find((e: any) => e.user?._id === currentUserId || e.user === currentUserId);
      const employeeId = emp ? (emp._id || emp.id) : currentUserId;

      const res = await apiClient.post(`/trainings/${trainingId}/enroll`, { employeeId });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to enroll');
    }
  }
);

export const updateTrainingProgressAsync = createAsyncThunk(
  'data/updateTrainingProgress',
  async (trainingId: string, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const currentUserId = state.auth.user?._id;
      const emp = state.data.employees.find((e: any) => e.user?._id === currentUserId || e.user === currentUserId);
      const employeeId = emp ? (emp._id || emp.id) : currentUserId;

      const res = await apiClient.put(`/trainings/${trainingId}/progress`, { employeeId });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update progress');
    }
  }
);

export const attendanceActionAsync = createAsyncThunk(
  'data/attendanceAction',
  async ({ employeeId, action }: { employeeId: string; action: 'check-in' | 'start-break' | 'end-break' | 'check-out' }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/attendance/action', { employeeId, action });
      return { record: res.data, action };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || `Failed to ${action}`);
    }
  }
);

export const runPayrollAsync = createAsyncThunk(
  'data/runPayroll',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/payroll/run-bulk');
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to run payroll');
    }
  }
);


const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    // Keep existing synchronous actions for components not yet refactored to use async thunks
    addUser(state, action: PayloadAction<User>) {
      state.users.push(action.payload);

    },
    removeUser(state, action: PayloadAction<string>) {
      state.users = state.users.filter(u => u.email !== action.payload);
    },
    addEmployee(state, action: PayloadAction<Employee>) {
      state.employees.push(action.payload);

    },
    removeEmployee(state, action: PayloadAction<string>) {
      state.employees = state.employees.filter(e => e.id !== action.payload);
    },
    updateLeaveStatus(state, action: PayloadAction<{ id: string; status: string }>) {
      const req = state.leaveRequests.find(r => r.id === action.payload.id);
      if (req) req.status = action.payload.status;
    },
    addLeaveRequest(state, action: PayloadAction<LeaveRequest>) {
      state.leaveRequests.unshift(action.payload);
    },
    addAttendanceRecord(state, action: PayloadAction<AttendanceRecord>) {
      state.attendance.unshift(action.payload);
    },
    addJob(state, action: PayloadAction<Job>) {
      state.jobs.push(action.payload);
    },
    moveCandidateStage(state, action: PayloadAction<{ id: string; stage: string }>) {
      const c = state.candidates.find(c => c.id === action.payload.id);
      if (c) c.stage = action.payload.stage;
    },
    toggleOnboardingTask(state, action: PayloadAction<{ joineeId: string; taskIdx: number }>) {
      const j = state.onboarding.find(o => o.id === action.payload.joineeId);
      if (j) {
        j.tasks[action.payload.taskIdx].done = !j.tasks[action.payload.taskIdx].done;
        j.progress = Math.round((j.tasks.filter(t => t.done).length / j.tasks.length) * 100);
      }
    },
    markNotificationRead(state, action: PayloadAction<string>) {
      const n = state.notifications.find(n => n.id === action.payload);
      if (n) n.read = true;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAllData.pending, state => { state.loading = true; state.error = null; })
      .addCase(fetchAllData.fulfilled, (state, action) => {
        state.loading = false;
        const d = action.payload;
        state.users = d.users || []; state.employees = d.employees || [];

        state.leaveRequests = (d.leaveRequests || []).map((lr: any) => {
          const start = new Date(lr.startDate);
          const end = new Date(lr.endDate);
          const diffDays = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
          return {
            id: lr._id || lr.id,
            empId: lr.employee?._id || lr.employee || '',
            name: lr.employee?.name || 'Unknown',
            type: lr.leaveType || lr.type || 'Casual',
            dates: `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`,
            startDate: lr.startDate,
            endDate: lr.endDate,
            days: diffDays,
            status: lr.status || 'Pending',
            initials: lr.employee?.initials || '?',
            avColor: lr.employee?.avColor || 'blue',
            reason: lr.reason || ''
          };
        });

        state.payroll = (d.payroll || []).map((p: any) => ({
          id: p._id || p.id,
          empId: p.employee?._id || p.employee,
          name: p.employee?.name || 'Unknown',
          dept: p.employee?.dept || 'Engineering',
          month: p.month,
          basic: p.basicSalary || 0,
          hra: p.hra || 0,
          allowances: p.allowances || 0,
          pf: p.pf || 0,
          tax: p.tax || 0,
          net: p.netSalary || 0,
          status: p.status || 'Pending'
        }));
        state.jobs = d.recruitment || d.jobs || [];
        state.candidates = d.candidates || []; state.performance = d.performance || [];

        state.training = (d.training || []).map((t: any) => ({
          id: t._id || t.id,
          name: t.title,
          category: t.category || 'Technical',
          enrolled: t.enrolledEmployees?.length || 0,
          completed: 0,
          deadline: t.endDate ? new Date(t.endDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '',
          instructor: t.instructor || 'Unknown',
          duration: "10h", // Placeholder or calculate from dates
          status: t.status || 'Upcoming',
          enrolledEmployees: t.enrolledEmployees || [],
          totalModules: t.totalModules || 5
        }));

        state.departments = d.departments || [];
        state.onboarding = d.onboarding || []; 
        state.onboardingConfig = d.onboardingConfig || null;
        state.notifications = d.notifications || [];

        const formatTime = (dt: any) => {
          if (!dt) return '--:--';
          return new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        };
        const formatDuration = (secs: number) => {
          const h = Math.floor(secs / 3600);
          const m = Math.floor((secs % 3600) / 60);
          return `${h}h ${m}m`;
        };

        state.attendance = (d.attendance || []).map((record: any) => {
          const id = record._id || record.id;
          let workSecs = 0;
          if (record.checkIn && record.checkOut) {
            const diffMs = new Date(record.checkOut).getTime() - new Date(record.checkIn).getTime();
            workSecs = Math.max(0, Math.floor(diffMs / 1000) - (record.totalBreakSec || 0));
          }
          return {
            id: id,
            _id: id,
            empId: record.employee?._id || record.employee,
            name: record.employee?.name || 'Unknown',
            date: new Date(record.date).toISOString().split('T')[0],
            clockIn: formatTime(record.checkIn),
            clockOut: formatTime(record.checkOut),
            workHrs: record.checkOut ? formatDuration(workSecs) : '0h 0m',
            breakHrs: formatDuration(record.totalBreakSec || 0),
            status: record.status || 'Present',
            initials: record.employee?.initials || '?',
            avColor: record.employee?.avColor || 'blue',
            sysId: record.employee?.id || '',
            rawCheckIn: record.checkIn,
            rawCheckOut: record.checkOut,
            breakStart: record.breakStart,
            totalBreakSec: record.totalBreakSec || 0
          };
        });
      })
      .addCase(fetchAllData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // createEmployeeAsync handlers
      .addCase(createEmployeeAsync.fulfilled, (state, action) => {
        state.employees.push(action.payload);
      })
      // updateLeaveStatusAsync handlers
      .addCase(updateLeaveStatusAsync.fulfilled, (state, action) => {
        const payloadId = action.payload._id || action.payload.id;
        const req = state.leaveRequests.find(r => r.id === payloadId);
        if (req) {
          req.status = action.payload.status;
        }
      })
      .addCase(removeEmployeeAsync.fulfilled, (state, action) => {
        state.employees = state.employees.filter((e: any) => e.id !== action.payload && e._id !== action.payload);
      })
      .addCase(addLeaveRequestAsync.fulfilled, (state, action) => {
        const lr = action.payload;
        const start = new Date(lr.startDate);
        const end = new Date(lr.endDate);
        const diffDays = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

        const emp = state.employees.find((e: any) => e._id === lr.employee || e.id === lr.employee);

        // In local state, push a frontend-formatted object so UI updates instantly
        state.leaveRequests.unshift({
          id: lr._id || lr.id,
          empId: lr.employee || '',
          name: emp?.name || 'Self',
          type: lr.leaveType || lr.type,
          dates: `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`,
          startDate: lr.startDate,
          endDate: lr.endDate,
          days: diffDays,
          status: lr.status || 'Pending',
          initials: emp?.initials || '?',
          avColor: emp?.avColor || 'blue',
          reason: lr.reason || ''
        });
      })
      .addCase(addJobAsync.fulfilled, (state, action) => {
        if (action.payload.job) {
          state.jobs.push(action.payload.job);
        } else {
          state.jobs.push(action.payload);
        }
        if (action.payload.notification) {
          state.notifications.unshift(action.payload.notification);
        }
      })
      .addCase(addTrainingProgramAsync.fulfilled, (state, action) => {
        const t = action.payload;
        state.training.push({
          id: t._id || t.id,
          name: t.title,
          category: t.category || 'Technical',
          enrolled: t.enrolledEmployees?.length || 0,
          completed: 0,
          deadline: t.endDate ? new Date(t.endDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '',
          instructor: t.instructor || 'Unknown',
          duration: "10h",
          status: t.status || 'Upcoming'
        });
      })
      .addCase(enrollTrainingAsync.fulfilled, (state, action) => {
        const t = action.payload;
        const index = state.training.findIndex(tr => tr.id === (t._id || t.id));
        if (index !== -1) {
          state.training[index].enrolledEmployees = t.enrolledEmployees;
          state.training[index].enrolled = t.enrolledEmployees?.length || 0;
        }
      })
      .addCase(updateTrainingProgressAsync.fulfilled, (state, action) => {
        const t = action.payload;
        const index = state.training.findIndex(tr => tr.id === (t._id || t.id));
        if (index !== -1) {
          state.training[index].enrolledEmployees = t.enrolledEmployees;
        }
      })
      .addCase(addCandidateAsync.fulfilled, (state, action) => {
        state.candidates.push(action.payload);
      })
      .addCase(moveCandidateStageAsync.fulfilled, (state, action) => {
        const payloadCandidate = action.payload.candidate || action.payload;
        const c = state.candidates.find(c => c.id === payloadCandidate.id || c.id === payloadCandidate._id);
        if (c) c.stage = payloadCandidate.stage;

        if (action.payload.onboarding) {
          state.onboarding.push(action.payload.onboarding);
        }
      })
      .addCase(toggleOnboardingTaskAsync.fulfilled, (state, action) => {
        const j = state.onboarding.find(o => o.id === action.payload.id);
        if (j) Object.assign(j, action.payload);
      })
      .addCase(toggleHrChecklistAsync.fulfilled, (state, action) => {
        state.onboardingConfig = action.payload;
      })
      .addCase(markNotificationReadAsync.fulfilled, (state, action) => {
        const n = state.notifications.find(n => n.id === action.payload.id);
        if (n) n.read = true;
      })
      .addCase(attendanceActionAsync.fulfilled, (state, actionPayload) => {
        const { record, action } = actionPayload.payload;
        const id = record._id || record.id;
        const existingIdx = state.attendance.findIndex((a: any) => a.id === id || a._id === id);

        const formatTime = (d: any) => {
          if (!d) return '--:--';
          return new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        };

        const formatDuration = (secs: number) => {
          const h = Math.floor(secs / 3600);
          const m = Math.floor((secs % 3600) / 60);
          return `${h}h ${m}m`;
        };

        const formattedRecord = {
          id: id,
          _id: id,
          empId: record.employee?._id || record.employee,
          name: record.employee?.name || 'Self',
          date: new Date(record.date).toISOString().split('T')[0],
          clockIn: formatTime(record.checkIn),
          clockOut: formatTime(record.checkOut),
          workHrs: '0h 0m',
          breakHrs: formatDuration(record.totalBreakSec || 0),
          status: record.status || 'Present',
          initials: '?',
          avColor: 'blue',
          rawCheckIn: record.checkIn,
          rawCheckOut: record.checkOut,
          breakStart: record.breakStart,
          totalBreakSec: record.totalBreakSec || 0
        };

        if (record.checkIn && record.checkOut) {
          const diffMs = new Date(record.checkOut).getTime() - new Date(record.checkIn).getTime();
          const workSecs = Math.max(0, Math.floor(diffMs / 1000) - (record.totalBreakSec || 0));
          formattedRecord.workHrs = formatDuration(workSecs);
        }

        if (existingIdx !== -1) {
          state.attendance[existingIdx] = { ...state.attendance[existingIdx], ...formattedRecord };
        } else {
          state.attendance.unshift(formattedRecord as any);
        }
      })
      .addCase(runPayrollAsync.fulfilled, (state, action) => {
        state.payroll = action.payload.map((p: any) => ({
          id: p._id || p.id,
          empId: p.employee?._id || p.employee,
          name: p.employee?.name || 'Unknown',
          dept: p.employee?.dept || 'Engineering',
          month: p.month,
          basic: p.basicSalary || 0,
          hra: p.hra || 0,
          allowances: p.allowances || 0,
          pf: p.pf || 0,
          tax: p.tax || 0,
          net: p.netSalary || 0,
          status: p.status || 'Pending'
        }));
      });
  },
});

export const { addUser, removeUser, addEmployee, removeEmployee, updateLeaveStatus, addLeaveRequest, addAttendanceRecord, addJob, moveCandidateStage, toggleOnboardingTask, markNotificationRead } = dataSlice.actions;
export default dataSlice.reducer;
